import { useEffect, useState } from "react";
import { Gift, Copy, Send, Wallet, TrendingUp, Lock, Unlock, Trophy, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/time";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

const GoldButton = ({ children, ...p }) => (
    <button {...p} className={`inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-[#C9A537] disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium uppercase tracking-[0.15em] ${p.className || ""}`}>{children}</button>
);
const GhostButton = ({ children, ...p }) => (
    <button {...p} className={`inline-flex items-center gap-2 px-4 py-2 border border-border hover:border-primary hover:text-primary disabled:opacity-40 text-xs uppercase tracking-[0.15em] ${p.className || ""}`}>{children}</button>
);

export default function Referrals() {
    const [data, setData] = useState(null);
    const [ledger, setLedger] = useState([]);
    const [redeemAmount, setRedeemAmount] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const [meR, ledR] = await Promise.all([
                api.get("/referrals/me"),
                api.get("/referrals/ledger"),
            ]);
            setData(meR.data);
            setLedger(ledR.data || []);
        } catch (e) { toast.error(apiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const copyShare = async () => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(data.share_url);
            } else {
                const ta = document.createElement("textarea");
                ta.value = data.share_url;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
            }
            toast.success("Referral link copied");
        } catch { toast.error("Could not copy — please long-press and copy the link above"); }
    };

    const shareWhatsApp = () => {
        const bonus = data.config.signup_bonus_enabled && data.config.signup_bonus > 0
            ? ` Plus you'll get a ₹${data.config.signup_bonus} AI credit on signup.`
            : "";
        const msg = `Hey! I'm using Law OS — an end-to-end practice management platform for advocates and law firms. Cases, hearings, AI-drafted documents, e-signatures, billing, all in one place.\n\nUse my invite link to claim a 14-day free trial:${bonus}\n\n${data.share_url}`;
        const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const unlockCoolingOff = async () => {
        setBusy(true);
        try {
            const { data: r } = await api.post("/referrals/unlock-cooling-off");
            if (r.unlocked_inr > 0) toast.success(`Unlocked ₹${r.unlocked_inr.toLocaleString()} into available`);
            else toast.info("Nothing ready to unlock yet — cooling-off window still active");
            load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    const redeem = async () => {
        const amt = parseFloat(redeemAmount);
        if (!amt || amt <= 0) { toast.error("Enter a positive amount"); return; }
        if (amt > data.balance.available) { toast.error(`Max available is ₹${data.balance.available}`); return; }
        setBusy(true);
        try {
            const { data: r } = await api.post("/referrals/redeem", { amount: amt, target: "ai_wallet" });
            toast.success(r.note);
            setRedeemAmount("");
            load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    const recentCtrl = useTableControls(data?.recent || [], {
        accessors: {
            status: (r) => r.converted ? "converted" : "signed_up",
            flag: (r) => r.suspicious ? "review" : "",
        },
    });
    const ledgerCtrl = useTableControls(ledger || []);

    if (!data) return null;

    return (
        <div className="space-y-8 max-w-6xl" data-testid="referrals-page">
            <div>
                <div className="overline">Earn with Law OS</div>
                <h1 className="display-display mt-1">Referral Program</h1>
                <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                    Refer fellow advocates and law firms to Law OS. You earn <span className="text-primary">{data.effective_pct}%</span> recurring commission on the first {data.config.max_cycles} subscription invoices the referred firm pays{data.config.signup_bonus_enabled && data.config.signup_bonus > 0 ? <>, plus a one-time ₹{data.config.signup_bonus} signup bonus per converted firm</> : null}. Commissions auto-credit to your firm&apos;s AI Wallet after the cooling-off window — wallet-only, no cash payouts.
                </p>
            </div>

            {/* Share card */}
            <div className="bg-card border border-border p-6 space-y-4" data-testid="referral-share-card">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <Gift className="w-5 h-5 text-primary" />
                        <div>
                            <div className="overline">Your referral code</div>
                            <div className="text-2xl font-serif text-foreground mt-1" data-testid="referral-code">{data.code}</div>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="overline">Tier</div>
                        <div className="text-primary mt-1 flex items-center gap-1.5 justify-end">
                            <Trophy className="w-4 h-4" />
                            <span className="text-sm">{data.tier} · {data.effective_pct}%</span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 bg-background border border-border px-3 py-2">
                    <code className="text-xs text-muted-foreground flex-1 truncate" data-testid="referral-share-url">{data.share_url}</code>
                    <button onClick={copyShare} data-testid="referral-copy-btn" className="text-primary hover:opacity-80" title="Copy link">
                        <Copy className="w-4 h-4" />
                    </button>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                    <button
                        onClick={shareWhatsApp}
                        data-testid="referral-whatsapp-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#25D366] text-foreground hover:bg-[#1DA851] text-xs font-medium uppercase tracking-[0.15em] transition-colors">
                        <MessageCircle className="w-3.5 h-3.5" />
                        Share on WhatsApp
                    </button>
                    <button
                        onClick={copyShare}
                        data-testid="referral-copy-secondary-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 border border-border hover:border-primary hover:text-primary text-xs uppercase tracking-[0.15em] transition-colors">
                        <Copy className="w-3.5 h-3.5" />
                        Copy link
                    </button>
                </div>
                <p className="text-[10px] text-muted-foreground">
                    Share this link with other firms. When they sign up and pay their first subscription invoice, you'll see {data.config.signup_bonus_enabled && data.config.signup_bonus > 0 ? "a signup bonus + " : ""}recurring commission appear in the ledger below.
                </p>
            </div>

            {/* Stats grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard label="Signups" value={data.stats.signups} testId="kpi-signups" icon={Send} />
                <KpiCard label="Conversions" value={data.stats.conversions} testId="kpi-conversions" icon={TrendingUp}
                         hint={`${data.stats.conversion_rate}% conversion`} />
                <KpiCard label="Pending" value={`₹${data.balance.pending.toLocaleString()}`} testId="kpi-pending" icon={Lock}
                         hint={`Cooling off · ${data.config.cooling_days}d`} />
                <KpiCard label="Available" value={`₹${data.balance.available.toLocaleString()}`} testId="kpi-available" icon={Wallet}
                         hint={`Redeemed ₹${data.balance.redeemed.toLocaleString()}`} />
            </div>

            {/* Cooling-off unlock + Redeem */}
            <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-card border border-border p-6 space-y-3" data-testid="cooling-off-card">
                    <div className="overline">Cooling-off sweep · auto wallet credit</div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                        Commissions accrue as <span className="text-primary">pending</span> for {data.config.cooling_days} days, then auto-credit straight into your firm&apos;s AI Wallet. No payouts as cash — wallet-only policy.
                    </p>
                    <GhostButton onClick={unlockCoolingOff} disabled={busy} data-testid="unlock-btn">
                        <Unlock className="w-3.5 h-3.5" />
                        {busy ? "Working…" : "Sweep pending → wallet"}
                    </GhostButton>
                </div>

                <div className="bg-card border border-border p-6 space-y-3" data-testid="redeem-card">
                    <div className="overline">Manual wallet credit</div>
                    <p className="text-xs text-muted-foreground">
                        Available: <span className="text-primary">₹{data.balance.available.toLocaleString()}</span>
                    </p>
                    <input
                        type="number" step="1" min="1"
                        value={redeemAmount}
                        onChange={(e) => setRedeemAmount(e.target.value)}
                        placeholder="Amount (INR)"
                        className="w-full bg-background border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none"
                        data-testid="redeem-amount"
                    />
                    <GoldButton onClick={redeem} disabled={busy || !redeemAmount} data-testid="redeem-btn">
                        {busy ? "Processing…" : "Credit to AI Wallet"}
                    </GoldButton>
                    <p className="text-[10px] text-muted-foreground">
                        Wallet-only. Referral commissions are not paid out as cash; they are credited 1:1 to your AI Wallet for spend on AI / eCourts / SaaS usage.
                    </p>
                </div>
            </div>

            {/* Recent referrals */}
            {data.recent.length > 0 && (
                <div className="bg-card border border-border p-6" data-testid="recent-referrals-card">
                    <div className="overline mb-4">Recent signups</div>
                    <FilterChip ctrl={recentCtrl} className="mb-2"/>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                                    <SortableTh field="new_user_email" ctrl={recentCtrl} className="py-2 pr-4">Email</SortableTh>
                                    <SortableTh field="created_at" ctrl={recentCtrl} className="py-2 pr-4">Signed up</SortableTh>
                                    <SortableTh field="status" ctrl={recentCtrl} className="py-2 pr-4">Status</SortableTh>
                                    <SortableTh field="flag" ctrl={recentCtrl} className="py-2 pr-4">Flag</SortableTh>
                                </tr>
                            </thead>
                            <tbody>
                                {recentCtrl.rows.map(r => (
                                    <tr key={r.id} className="border-b border-border" data-testid={`referral-row-${r.id}`}>
                                        <td className="py-2 pr-4 text-foreground">{r.new_user_email}</td>
                                        <td className="py-2 pr-4 text-muted-foreground text-xs">{fmtDateTime(r.created_at)}</td>
                                        <td className="py-2 pr-4">
                                            <span className={r.converted ? "text-emerald-400" : "text-muted-foreground"}>
                                                {r.converted ? "● Converted" : "○ Signed up"}
                                            </span>
                                        </td>
                                        <td className="py-2 pr-4">
                                            {r.suspicious ? <span className="text-amber-400 text-[10px]">⚠ Review</span> : <span className="text-muted-foreground text-[10px]">—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Ledger */}
            <div className="bg-card border border-border p-6" data-testid="ledger-card">
                <div className="overline mb-4">Commission ledger</div>
                {ledger.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No transactions yet. Share your link and start earning.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                                    <SortableTh field="at" ctrl={ledgerCtrl} className="py-2 pr-4">When</SortableTh>
                                    <SortableTh field="type" ctrl={ledgerCtrl} className="py-2 pr-4">Type</SortableTh>
                                    <SortableTh field="amount" ctrl={ledgerCtrl} className="py-2 pr-4">Amount</SortableTh>
                                    <SortableTh field="status" ctrl={ledgerCtrl} className="py-2 pr-4">Status</SortableTh>
                                    <SortableTh field="note" ctrl={ledgerCtrl} className="py-2 pr-4">Note</SortableTh>
                                </tr>
                            </thead>
                            <tbody>
                                {ledgerCtrl.rows.map(l => (
                                    <tr key={l.id} className="border-b border-border">
                                        <td className="py-2 pr-4 text-muted-foreground text-xs">{fmtDateTime(l.at)}</td>
                                        <td className="py-2 pr-4 capitalize">{l.type?.replaceAll("_", " ")}</td>
                                        <td className={`py-2 pr-4 ${l.amount < 0 ? "text-red-400" : "text-primary"}`}>
                                            ₹{Math.abs(l.amount).toLocaleString()}
                                        </td>
                                        <td className="py-2 pr-4 text-muted-foreground text-xs capitalize">{l.status?.replaceAll("_", " ")}</td>
                                        <td className="py-2 pr-4 text-muted-foreground text-xs">{l.note}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

const KpiCard = ({ label, value, hint, icon: Icon, testId }) => (
    <div className="bg-card border border-border p-5" data-testid={testId}>
        <div className="flex items-center justify-between mb-2">
            <div className="overline">{label}</div>
            <Icon className="w-4 h-4 text-primary" />
        </div>
        <div className="text-2xl font-serif text-foreground">{value}</div>
        {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
    </div>
);
